import React, { useEffect, useState, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@chakra-ui/react';
import getCroppedImg from '../utils/cropImage';
import imageCompression from 'browser-image-compression';
import { Box, Card, CardHeader, CardBody, Heading, Text, Avatar, VStack, Divider, Button, HStack, useToast, Spinner, Tag, Table, Thead, Tbody, Tr, Th, Td, Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverHeader, PopoverBody } from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, associateAPI, designationsAPI, userAPI, requestsAPI } from '../services/api';

// Add window.userAPI type for TS
declare global {
  interface Window {
    userAPI?: {
      uploadPhoto?: (userId: string, photo: string) => Promise<void>;
      removePhoto?: (userId: string) => Promise<void>;
    };
  }
}


const Profile: React.FC = () => {
  const { user, setUser } = useAuth();
  const [assignedProjects, setAssignedProjects] = useState<any[]>([]);
  const [allAssociates, setAllAssociates] = useState<any[]>([]);
  const [associate, setAssociate] = useState<any>(null);
  const [designations, setDesignations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [myRequests, setMyRequests] = useState<any[]>([]);
  const [projectsMap, setProjectsMap] = useState<{ [key: string]: string }>({});
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);


  // Assignment request modal state for associates (must be at top level)
  const [requestAssignmentOpen, setRequestAssignmentOpen] = useState(false);
  const [selectedAssignmentProjectId, setSelectedAssignmentProjectId] = useState<string | null>(null);
  const [assignmentRequestReason, setAssignmentRequestReason] = useState<string>('');
  const [allProjects, setAllProjects] = useState<any[]>([]);

  // Fetch all projects for assignment modal
  useEffect(() => {
    if (!user?.associateId) return;
    const fetchAllProjects = async () => {
      try {
        const res = await projectsAPI.getAll();
  setAllProjects(res.data as any[]);
      } catch {}
    };
    fetchAllProjects();
  }, [user]);

  // Handler for sending assignment request
  const handleSendAssignmentRequest = async () => {
    if (!selectedAssignmentProjectId || !assignmentRequestReason.trim() || !user) return;
    try {
      const project = allProjects.find((p: any) => p._id === selectedAssignmentProjectId);
      await requestsAPI.create({
        projectId: selectedAssignmentProjectId,
        associateId: user.associateId,
        associateName: associate?.name,
        client: project?.client || '-',
        reason: assignmentRequestReason.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
        type: 'assignment',
      });
      toast({ status: 'success', title: 'Assignment request sent to Finance team!' });
      setRequestAssignmentOpen(false);
      setSelectedAssignmentProjectId(null);
      setAssignmentRequestReason('');
    } catch (err) {
      toast({ status: 'error', title: 'Failed to send assignment request' });
    }
  };

  // Helper to render profile photo section for all users (must be after state declarations)
  const renderProfilePhotoSection = () => {
  // @ts-ignore: avatar may exist on user
  const profilePhoto = user && user.associateId ? associate?.photo : (user ? (user as any).avatar : '');
    const profileName = user && user.associateId ? associate?.name : user?.name;
    return (
      <Box position="relative">
        <Avatar
          name={profileName}
          src={profilePhoto || undefined}
          size="2xl"
          borderWidth={2}
          borderColor="primary.400"
          mb={2}
        />
        <HStack justify="center" mt={2}>
          <Button
            size="sm"
            colorScheme="primary"
            onClick={() => fileInputRef.current?.click()}
            isLoading={photoUploading}
            variant="outline"
          >
            Upload Photo
          </Button>
          {profilePhoto && (
            <Button
              size="sm"
              colorScheme="red"
              onClick={handleRemovePhoto}
              isLoading={photoUploading}
              variant="ghost"
            >
              Remove
            </Button>
          )}
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handlePhotoChange}
          />
          {/* Crop Modal */}
          <Modal isOpen={cropModalOpen} onClose={() => setCropModalOpen(false)} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Crop Profile Photo</ModalHeader>
              <ModalBody>
                {selectedImage && (
                  <Box position="relative" w="100%" h="300px" bg="gray.100">
                    <Cropper
                      image={URL.createObjectURL(selectedImage)}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </Box>
                )}
              </ModalBody>
              <ModalFooter>
                <Button onClick={() => setCropModalOpen(false)} mr={3} variant="ghost">Cancel</Button>
                <Button colorScheme="primary" onClick={handleCropSave} isLoading={photoUploading}>Save</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </HStack>
      </Box>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [designationRes] = await Promise.all([
          designationsAPI.getAll()
        ]);
        setDesignations(designationRes.data);
        if (user?.associateId) {
          const [associateRes, projectsRes, associatesRes] = await Promise.all([
            associateAPI.getAssociateById(user.associateId),
            projectsAPI.getAll(),
            associateAPI.getAllAssociates(),
          ]);
          setAssociate(associateRes.data);
          setAllAssociates(associatesRes.data);
          const assigned = projectsRes.data.filter((p: any) => Array.isArray(p.associates) && p.associates.includes(user.associateId));
          setAssignedProjects(assigned);
        } else {
          // For users without associateId (e.g., managers)
          const [projectsRes, associatesRes] = await Promise.all([
            projectsAPI.getAll(),
            associateAPI.getAllAssociates(),
          ]);
          setAllAssociates(associatesRes.data);
          // Find projects where user is manager (by name or _id)
          const assigned = projectsRes.data.filter((p: any) =>
            user &&
            ((p.managerId && (p.managerId === user._id || p.managerId === user._id)) ||
            (p.managerName && p.managerName === user.name))
          );
          setAssignedProjects(assigned);
        }
      } catch (err) {
        toast({ status: 'error', title: 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, toast]);

  // Request modal state and handlers (must be at top level)
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    setSelectedImage(e.target.files[0]);
    setCropModalOpen(true);
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    setPhotoUploading(true);
    try {
      const cropped = await getCroppedImg(selectedImage, croppedAreaPixels);
      const options = { maxSizeMB: 1, maxWidthOrHeight: 800, useWebWorker: true };
      const compressedFile = await imageCompression(cropped, options);
      if (compressedFile.size > 1024 * 1024) {
        toast({ status: 'error', title: 'Image too large even after compression. Please choose a smaller image (max 1MB).' });
        setPhotoUploading(false);
        setCropModalOpen(false);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          if (user && user.associateId && associate?._id) {
            await associateAPI.uploadPhoto(associate._id, reader.result as string);
            setAssociate((prev: any) => ({ ...prev, photo: reader.result }));
            window.dispatchEvent(new Event('profile-photo-updated'));
          } else if (user && user._id) {
            await userAPI.uploadPhoto(user._id, reader.result as string);
            // Update user context and localStorage
            const updatedUser = { ...user, avatar: reader.result as string };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
          toast({ status: 'success', title: 'Profile photo updated' });
        } catch {
          toast({ status: 'error', title: 'Failed to upload photo' });
        } finally {
          setPhotoUploading(false);
          setCropModalOpen(false);
        }
      };
      reader.readAsDataURL(compressedFile);
    } catch {
      toast({ status: 'error', title: 'Failed to crop/compress image' });
      setPhotoUploading(false);
      setCropModalOpen(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoUploading(true);
    try {
      if (user && user.associateId && associate?._id) {
        await associateAPI.removePhoto(associate._id);
        setAssociate((prev: any) => ({ ...prev, photo: '' }));
        window.dispatchEvent(new Event('profile-photo-updated'));
      } else if (user && user._id) {
        await userAPI.removePhoto(user._id);
        // Update user context and localStorage
        const updatedUser = { ...user, avatar: '' };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      toast({ status: 'success', title: 'Profile photo removed' });
    } catch {
      toast({ status: 'error', title: 'Failed to remove photo' });
    } finally {
      setPhotoUploading(false);
    }
  };

  // Request modal state and handlers (move to top level)
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [requestReason, setRequestReason] = useState('');

  const handleOpenRequestModal = (project: any) => {
    setSelectedProject(project);
    setRequestReason('');
    setRequestModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setRequestModalOpen(false);
    setSelectedProject(null);
    setRequestReason('');
  };

  const handleSendRequest = async () => {
    if (!selectedProject || !requestReason.trim() || !user) return;
    try {
      await requestsAPI.create({
        projectId: selectedProject._id,
        managerId: user._id,
        managerName: user.name,
        client: selectedProject.client?.name || selectedProject.client || '-',
        reason: requestReason.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      toast({ status: 'success', title: 'Request sent to Finance team!' });
      handleCloseRequestModal();
    } catch (err) {
      toast({ status: 'error', title: 'Failed to send request' });
    }
  };

  useEffect(() => {
    if (!user) return;
    const fetchMyRequests = async () => {
      try {
        const res = await requestsAPI.getAll();
        // Show requests where this user is the manager or associate
        setMyRequests(res.data.filter((r: any) => r.managerId === user._id || r.associateId === user.associateId));
      } catch {
        setMyRequests([]);
      }
    };
    fetchMyRequests();
  }, [user]);

  useEffect(() => {
    // Fetch all projects for mapping projectId to project name
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.getAll();
        const map: { [key: string]: string } = {};
        res.data.forEach((p: any) => {
          map[p._id] = p.name;
          if (p.projectId) map[p.projectId] = p.name; // for numeric/project code keys
        });
        setProjectsMap(map);
      } catch {}
    };
    fetchProjects();
  }, []);

  if (!user) return <Box p={8}><Text>User not found.</Text></Box>;
  if (loading) return <Box p={8} textAlign="center"><Spinner size="xl" /></Box>;

  // If associateId exists, show associate profile view
  if (user.associateId) {
    return (
  <Box p={8} maxW="1200px" mx="auto">
        <Card boxShadow="lg" borderRadius="2xl">
          <CardHeader pb={0}>
            <Heading size="lg">Profile</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="center" spacing={6} w="full">
              {renderProfilePhotoSection()}
              <Box w="100%" textAlign="left">
                <Heading size="md" mb={2}>Associate Details</Heading>
                <Divider mb={3} />
                <VStack align="start" spacing={1} fontSize="md">
                  <HStack><Text fontWeight="bold">Name:</Text><Text>{associate?.name}</Text></HStack>
                  <HStack><Text fontWeight="bold">Email:</Text><Text>{associate?.email}</Text></HStack>
                  {/* Removed duplicate phone field */}
                    <HStack><Text fontWeight="bold">Phone:</Text><Text>{associate?.phone ? associate.phone.replace(/^\+91(\d{10})$/, '+91-$1') : '-'}</Text></HStack>
                  <HStack><Text fontWeight="bold">Department:</Text><Text>{associate?.department}</Text></HStack>
                  <HStack><Text fontWeight="bold">Position:</Text><Text>{associate?.position}</Text></HStack>
                  <HStack><Text fontWeight="bold">Join Date:</Text><Text>{associate?.joinDate}</Text></HStack>
                  <HStack><Text fontWeight="bold">Status:</Text><Tag colorScheme={associate?.status === 'active' ? 'green' : 'gray'}>{associate?.status}</Tag></HStack>
                  <HStack><Text fontWeight="bold">Skills:</Text><Text>{associate?.skills || '-'}</Text></HStack>
                  <HStack><Text fontWeight="bold">Experience:</Text><Text>{associate?.experience || '-'}</Text></HStack>
                  <HStack><Text fontWeight="bold">Education:</Text><Text>{associate?.education || '-'}</Text></HStack>
                  <HStack><Text fontWeight="bold">Languages:</Text><Text>{associate?.languages || '-'}</Text></HStack>
                  <HStack><Text fontWeight="bold">Emergency Contact:</Text><Text>{associate?.emergencyContact?.name} ({associate?.emergencyContact?.contactNumber ? associate.emergencyContact.contactNumber.replace(/^\+91(\d{10})$/, '+91-$1') : associate?.emergencyContact?.contactNumber || '-'})</Text></HStack>
                </VStack>
              </Box>
              <Divider />
              <Box w="100%">
                <Heading size="md" mb={2}>Assigned Projects</Heading>
                <Divider mb={3} />
                {assignedProjects.length === 0 && (
                  <Box>
                    <Text>No projects assigned.</Text>
                    <Button mt={4} colorScheme="blue" onClick={() => setRequestAssignmentOpen(true)}>
                      Request Assignment to Project
                    </Button>
                    {/* Assignment Request Modal */}
                    <Modal isOpen={requestAssignmentOpen} onClose={() => setRequestAssignmentOpen(false)} size="md">
                      <ModalOverlay />
                      <ModalContent>
                        <ModalHeader>Request Assignment to Project</ModalHeader>
                        <ModalBody>
                          <Text mb={2}>Select Project:</Text>
                          <select
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '12px' }}
                            value={selectedAssignmentProjectId || ''}
                            onChange={e => setSelectedAssignmentProjectId(e.target.value || null)}
                          >
                            <option value="">-- Select Project --</option>
                            {allProjects
                              .filter((p: any) => Array.isArray(p.associates) && !p.associates.includes(user.associateId))
                              .map((p: any) => (
                                <option key={p._id} value={p._id}>{p.name} ({p.client?.name || p.client || '-'})</option>
                              ))}
                          </select>
                          <Text mb={2}>Reason for Request:</Text>
                          <textarea
                            style={{ width: '100%', minHeight: '80px', borderRadius: '8px', border: '1px solid #ccc', padding: '8px' }}
                            value={assignmentRequestReason}
                            onChange={e => setAssignmentRequestReason(e.target.value)}
                            placeholder="Enter your request reason here..."
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button colorScheme="blue" mr={3} onClick={handleSendAssignmentRequest} isDisabled={!selectedAssignmentProjectId || !assignmentRequestReason.trim()}>
                            Send Request
                          </Button>
                          <Button variant="ghost" onClick={() => setRequestAssignmentOpen(false)}>Cancel</Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  </Box>
                )}
                {assignedProjects.length > 0 && (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Project Name</Th>
                          <Th>Client Name</Th>
                          <Th>Start Date</Th>
                          <Th>End Date</Th>
                          <Th>Team Members</Th>
                          <Th>Manager Name</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assignedProjects.map((p: any) => (
                          <Tr key={p._id}>
                            <Td>{p.name}</Td>
                            <Td>{p.client?.name || p.client || p.clientId || '-'}</Td>
                            <Td>{p.startDate}</Td>
                            <Td>{p.endDate}</Td>
                            <Td>
                              {Array.isArray(p.associates) && p.associates.length > 0
                                ? p.associates.map((id: string, idx: number) => {
                                    const found = allAssociates.find((a: any) => a._id === id);
                                    return found ? (
                                      <React.Fragment key={id}>
                                        <Popover trigger="hover" placement="top">
                                          <PopoverTrigger>
                                            <Text as="span" color="blue.600" cursor="pointer" textDecor="underline" mx={1}>
                                              {found.name}
                                            </Text>
                                          </PopoverTrigger>
                                          <PopoverContent minW="250px">
                                            <PopoverArrow />
                                            <PopoverHeader fontWeight="bold">{found.name}</PopoverHeader>
                                            <PopoverBody>
                                              <VStack align="start" spacing={1} fontSize="sm">
                                                <Text><b>Email:</b> {found.email || '-'}</Text>
                                                  <Text><b>Phone:</b> {found.phone ? found.phone.replace(/^\+91(\d{10})$/, '+91-$1') : '-'}</Text>
                                                <Text><b>Department:</b> {found.department || '-'}</Text>
                                                <Text><b>Designation:</b> {
                                                  (designations.find((d: any) => String(d._id) === String(found.designation))?.title || '-')
                                                }</Text>
                                                <Text><b>Skills:</b> {found.skills && found.skills.trim() !== '' ? found.skills : '-'}</Text>
                                                <Text><b>Status:</b> {found.status || '-'}</Text>
                                                <Text><b>Join Date:</b> {found.joinDate || '-'}</Text>
                                              </VStack>
                                            </PopoverBody>
                                          </PopoverContent>
                                        </Popover>
                                        {idx < p.associates.length - 1 && <span>, </span>}
                                      </React.Fragment>
                                    ) : (
                                      <React.Fragment key={id}>
                                        <Text as="span">{id}</Text>{idx < p.associates.length - 1 && <span>, </span>}
                                      </React.Fragment>
                                    );
                                  })
                                : '-'}
                            </Td>
                            <Td>{p.managerName || '-'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
                {/* Always show My Requests table for associates */}
                <Box w="100%" mt={8}>
                  <Heading size="md" mb={2}>My Requests</Heading>
                  <Divider mb={3} />
                  {myRequests.length === 0 ? (
                    <Text>No requests found.</Text>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white rounded shadow">
                        <thead>
                          <tr>
                            <th className="px-4 py-2">Project</th>
                            <th className="px-4 py-2">Reason</th>
                            <th className="px-4 py-2">Status</th>
                            <th className="px-4 py-2">Created</th>
                            <th className="px-4 py-2">Reject Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myRequests.map((req: any) => (
                            <tr key={req._id}>
                              <td className="border px-4 py-2">{projectsMap[req.projectId] || req.projectId}</td>
                              <td className="border px-4 py-2">{req.reason}</td>
                              <td className="border px-4 py-2">{req.status}</td>
                              <td className="border px-4 py-2">{new Date(req.createdAt).toLocaleString()}</td>
                              <td className="border px-4 py-2">{req.rejectReason || (req.status === 'rejected' ? 'No reason provided' : '')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Box>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // For users without associateId (e.g., managers)
  return (
  <Box p={8} maxW="1200px" mx="auto">
      <Card boxShadow="lg" borderRadius="2xl">
        <CardHeader pb={0}>
          <Heading size="lg">Profile</Heading>
        </CardHeader>
        <CardBody>
          <VStack align="center" spacing={6} w="full">
            {renderProfilePhotoSection()}
            <Box w="100%" textAlign="left">
              <Heading size="md" mb={2}>User Details</Heading>
              <Divider mb={3} />
              <VStack align="start" spacing={1} fontSize="md">
                <HStack><Text fontWeight="bold">Name:</Text><Text>{user.name}</Text></HStack>
                <HStack><Text fontWeight="bold">Email:</Text><Text>{user.email}</Text></HStack>
                <HStack><Text fontWeight="bold">Role:</Text><Text>{user.role}</Text></HStack>
                <HStack><Text fontWeight="bold">Status:</Text><Text>{user.status || '-'}</Text></HStack>
                <HStack><Text fontWeight="bold">Join Date:</Text><Text>{user.joinDate || '-'}</Text></HStack>
              </VStack>
            </Box>
            <Divider />
            <Box w="100%">
              <Heading size="md" mb={2}>Assigned Projects</Heading>
              <Divider mb={3} />
              {assignedProjects.length === 0 ? (
                <Text>No projects assigned.</Text>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Project Name</Th>
                        <Th>Client Name</Th>
                        <Th>Start Date</Th>
                        <Th>End Date</Th>
                        <Th>Team Members</Th>
                        <Th>Manager Name</Th>
                        <Th>Request</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {assignedProjects.map((p: any) => (
                        <Tr key={p._id}>
                          <Td>{p.name}</Td>
                          <Td>{p.client?.name || p.client || p.clientId || '-'}</Td>
                          <Td>{p.startDate}</Td>
                          <Td>{p.endDate}</Td>
                          <Td>
                            {Array.isArray(p.associates) && p.associates.length > 0
                              ? p.associates.map((id: string, idx: number) => {
                                  const found = allAssociates.find((a: any) => a._id === id);
                                  return found ? (
                                    <Popover trigger="hover" placement="top" key={id}>
                                      <PopoverTrigger>
                                        <Text as="span" color="blue.600" cursor="pointer" textDecor="underline" mx={1}>
                                          {found.name}{idx < p.associates.length - 1 ? ',' : ''}
                                        </Text>
                                      </PopoverTrigger>
                                      <PopoverContent minW="250px">
                                        <PopoverArrow />
                                        <PopoverHeader fontWeight="bold">{found.name}</PopoverHeader>
                                        <PopoverBody>
                                          <VStack align="start" spacing={1} fontSize="sm">
                                            <Text><b>Email:</b> {found.email || '-'}</Text>
                                            <Text><b>Phone:</b> {found.phone || '-'}</Text>
                                            <Text><b>Department:</b> {found.department || '-'}</Text>
                                            <Text><b>Designation:</b> {
                                              (designations.find((d: any) => String(d._id) === String(found.designation))?.title || '-')
                                            }</Text>
                                            <Text><b>Skills:</b> {found.skills && found.skills.trim() !== '' ? found.skills : '-'}</Text>
                                            <Text><b>Status:</b> {found.status || '-'}</Text>
                                            <Text><b>Join Date:</b> {found.joinDate || '-'}</Text>
                                          </VStack>
                                        </PopoverBody>
                                      </PopoverContent>
                                    </Popover>
                                  ) : (
                                    <Text as="span" key={id}>{id}{idx < p.associates.length - 1 ? ',' : ''}</Text>
                                  );
                                })
                              : '-'}
                          </Td>
                          <Td>{p.managerName || '-'}</Td>
                          <Td>
                            <Button size="sm" colorScheme="blue" onClick={() => handleOpenRequestModal(p)}>
                              Request
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {/* Request Modal */}
                  <Modal isOpen={requestModalOpen} onClose={handleCloseRequestModal} size="md">
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>Send Request for Project</ModalHeader>
                      <ModalBody>
                        <Text mb={2}>Project: <b>{selectedProject?.name}</b></Text>
                        <Text mb={2}>Client: <b>{selectedProject?.client?.name || selectedProject?.client || '-'}</b></Text>
                        <Text mb={2}>Reason for Request:</Text>
                        <textarea
                          style={{ width: '100%', minHeight: '80px', borderRadius: '8px', border: '1px solid #ccc', padding: '8px' }}
                          value={requestReason}
                          onChange={e => setRequestReason(e.target.value)}
                          placeholder="Enter your request reason here..."
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleSendRequest} isDisabled={!requestReason.trim()}>
                          Send Request
                        </Button>
                        <Button variant="ghost" onClick={handleCloseRequestModal}>Cancel</Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </Box>

              )}
            </Box>
            <Box w="100%" mt={8}>
              <Heading size="md" mb={2}>My Requests</Heading>
              <Divider mb={3} />
              {myRequests.length === 0 ? (
                <Text>No requests found.</Text>
              ) : (
                <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded shadow">
        <thead>
          <tr>
            <th className="px-4 py-2">Project</th>
            <th className="px-4 py-2">Client</th>
            <th className="px-4 py-2">Reason</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Created</th>
            <th className="px-4 py-2">Reject Reason</th>
          </tr>
        </thead>
        <tbody>
          {myRequests.map((req: any) => (
            <tr key={req._id}>
              <td className="border px-4 py-2">{projectsMap[req.projectId] || req.projectId}</td>
              <td className="border px-4 py-2">{req.client}</td>
              <td className="border px-4 py-2">{req.reason}</td>
              <td className="border px-4 py-2">{req.status}</td>
              <td className="border px-4 py-2">{new Date(req.createdAt).toLocaleString()}</td>
              <td className="border px-4 py-2">{req.rejectReason || (req.status === 'rejected' ? 'No reason provided' : '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
              )}
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
}

export default Profile;
